import React, { useState, useEffect, useMemo, useRef } from 'react';
import { updateDoc, doc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useLiveCollection } from '../../utils/liveData';
import { createWhatsAppURL, normalizePhoneForWhatsApp } from '../../utils/whatsappUtils';
import { KESIF_REMINDER_START_DATE as REMINDER_START_DATE, ORDER_STATUS } from '../../constants';
import {
  CalendarCheckIcon, ChevronDownIcon, CheckIcon, PhoneIcon, MessageIcon,
  MapPinIcon, ClockIcon, UndoIcon, AlertIcon, NavigationIcon
} from '../common/Icons';
import './PlanReminders.css';

const OPEN_STORAGE_KEY = 'planRemindersOpen';
const FILTER_STORAGE_KEY = 'planRemindersFilter';
const UNDO_DURATION = 6000;
const COMPLETE_ANIMATION = 320;
const DAY_NAMES = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'];
const DAY_SHORT = ['Paz', 'Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt'];
const MONTH_SHORT = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];

// Keşif: keşif tarihi/saati. Montaj: teslimat tarihi/saati.
const TYPES = {
  kesif: {
    label: 'Keşif',
    dateField: 'inspectionDate',
    timeField: 'inspectionTime',
    doneField: 'inspectionDone',
    doneAtField: 'inspectionDoneAt',
    doneByField: 'inspectionDoneBy',
    messageNoun: 'keşif',
    doneNoun: 'keşfi',
  },
  montaj: {
    label: 'Montaj',
    dateField: 'deliveryDate',
    timeField: 'deliveryTime',
    doneField: 'montajDone',
    doneAtField: 'montajDoneAt',
    doneByField: 'montajDoneBy',
    messageNoun: 'montaj',
    doneNoun: 'montajı',
  },
};

const FILTERS = [
  { key: 'all', label: 'Tümü' },
  { key: 'kesif', label: 'Keşif' },
  { key: 'montaj', label: 'Montaj' },
];

// Tarihler formdaki date input'tan "YYYY-MM-DD" olarak kaydediliyor
const toDateKey = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

const parseDateKey = (key) => {
  const [y, m, d] = (key || '').split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

const formatDate = (date) =>
  `${date.getDate()}.${String(date.getMonth() + 1).padStart(2, '0')}.${date.getFullYear()}`;

const formatShortDate = (date) => `${date.getDate()} ${MONTH_SHORT[date.getMonth()]} ${DAY_SHORT[date.getDay()]}`;

const getStatus = (diffDays, timePassed) => {
  if (diffDays < 0) return { label: `${-diffDays} gün gecikti`, tone: 'danger' };
  if (timePassed) return { label: 'Saati geçti', tone: 'danger' };
  if (diffDays === 0) return { label: 'Bugün', tone: 'accent' };
  if (diffDays === 1) return { label: 'Yarın', tone: 'info' };
  return { label: `${diffDays} gün sonra`, tone: 'neutral' };
};

// Saati olmayanlar gün içinde en sona
const getSortKey = (item) => `${item.dateKey} ${item.time || '99:99'}`;

// Keşif mesajında saat, 2 saatlik dilim olarak verilir: "11:00" -> "11:00-13:00"
const KESIF_TIME_WINDOW_HOURS = 2;
const formatTimeWindow = (time) => {
  const match = /^(\d{1,2}):(\d{2})/.exec(time || '');
  if (!match) return time;
  const endHour = (parseInt(match[1], 10) + KESIF_TIME_WINDOW_HOURS) % 24;
  return `${match[1].padStart(2, '0')}:${match[2]}-${String(endHour).padStart(2, '0')}:${match[2]}`;
};

const buildReminderMessage = (item) => {
  const timeText = !item.time
    ? ' için'
    : item.type === 'kesif'
      ? ` ${formatTimeWindow(item.time)} saatleri arasında`
      : ` saat ${item.time} için`;
  return `Merhaba ${item.name || ''},\n\n` +
    `${formatDate(item.date)} ${DAY_NAMES[item.date.getDay()]} günü${timeText} ` +
    `planlanan ${TYPES[item.type].messageNoun} randevunuzu hatırlatmak isteriz. ` +
    `Herhangi bir değişiklik olursa lütfen bize bildiriniz.\n\n` +
    `Ay Home Lotus`;
};

const readStored = (key, fallback) => {
  try {
    return localStorage.getItem(key) ?? fallback;
  } catch {
    return fallback;
  }
};

const writeStored = (key, value) => {
  try { localStorage.setItem(key, value); } catch { /* yoksay */ }
};

const wait = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// Keşif veya montaj tarihi olan müşteriler: paylaşılan canlı müşteri listesinden filtrelenir
// (ayrı sorgu açmak aynı müşterileri ikinci kez okutur ve her açılışta yeniden ücretlendirilir)
const useLiveCustomers = (field, enabled) => {
  const { items, loading, error } = useLiveCollection('customers', enabled);
  return useMemo(() => {
    if (!enabled) return { docs: [], loading: false, error: null };
    if (error) {
      console.error(`Hatırlatmalar alınamadı (${field}):`, error);
      return { docs: [], loading: false, error: 'Hatırlatmalar yüklenemedi.' };
    }
    const docs = (items || [])
      .filter(c => typeof c[field] === 'string' && c[field] >= REMINDER_START_DATE)
      .sort((a, b) => a[field].localeCompare(b[field]));
    return { docs, loading, error: null };
  }, [items, loading, error, field, enabled]);
};

const PlanReminders = ({ user, onlyOwnData = false, allowedTypes = ['kesif', 'montaj'], defaultFilter = 'all' }) => {
  const showKesif = allowedTypes.includes('kesif');
  const showMontaj = allowedTypes.includes('montaj');
  const singleType = allowedTypes.length === 1 ? allowedTypes[0] : null;

  const kesifQuery = useLiveCustomers('inspectionDate', showKesif);
  const montajQuery = useLiveCustomers('deliveryDate', showMontaj);

  const [open, setOpen] = useState(() => readStored(OPEN_STORAGE_KEY, '1') !== '0');
  const [filter, setFilter] = useState(() => {
    if (singleType) return singleType;
    const stored = readStored(FILTER_STORAGE_KEY, defaultFilter);
    return FILTERS.some(f => f.key === stored) ? stored : defaultFilter;
  });
  const [completingIds, setCompletingIds] = useState([]);
  const [pendingIds, setPendingIds] = useState([]);
  const [undoItem, setUndoItem] = useState(null);
  const [actionError, setActionError] = useState(null);
  const undoTimerRef = useRef(null);
  const todayKey = toDateKey(new Date());

  useEffect(() => () => clearTimeout(undoTimerRef.current), []);

  const loading = kesifQuery.loading || montajQuery.loading;
  const error = kesifQuery.error || montajQuery.error;

  const allItems = useMemo(() => {
    const today = parseDateKey(todayKey);
    const now = new Date();
    const nowTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    const build = (docs, type) => {
      const cfg = TYPES[type];
      return docs
        .filter(c => !c[cfg.doneField])
        .filter(c => type !== 'montaj' || c.orderStatus !== ORDER_STATUS.DELIVERED)
        .filter(c => !onlyOwnData || c.createdBy === user?.uid)
        .map(c => {
          const date = parseDateKey(c[cfg.dateField]);
          if (!date) return null;
          const diffDays = Math.round((date - today) / (1000 * 60 * 60 * 24));
          const time = c[cfg.timeField] || '';
          const timePassed = diffDays === 0 && !!time && time < nowTime;
          return {
            key: `${type}-${c.id}`,
            customerId: c.id,
            type,
            name: c.name,
            phone1: c.phone1,
            address: c.address,
            dateKey: c[cfg.dateField],
            time,
            date,
            diffDays,
            status: getStatus(diffDays, timePassed),
          };
        })
        .filter(Boolean);
    };
    return [...build(kesifQuery.docs, 'kesif'), ...build(montajQuery.docs, 'montaj')]
      .filter(item => !pendingIds.includes(item.key))
      .sort((a, b) => getSortKey(a).localeCompare(getSortKey(b)));
  }, [kesifQuery.docs, montajQuery.docs, pendingIds, onlyOwnData, user?.uid, todayKey]);

  const counts = useMemo(() => ({
    all: allItems.length,
    kesif: allItems.filter(i => i.type === 'kesif').length,
    montaj: allItems.filter(i => i.type === 'montaj').length,
  }), [allItems]);

  const { overdue, upcoming } = useMemo(() => {
    const visible = allItems.filter(i => filter === 'all' || i.type === filter);
    const result = { overdue: [], upcoming: [] };
    visible.forEach(i => (i.diffDays < 0 ? result.overdue : result.upcoming).push(i));
    // Gecikenlerde en çok geciken gün en üstte, aynı gün içinde saat sırası korunur
    result.overdue.sort((a, b) => a.diffDays - b.diffDays);
    return result;
  }, [allItems, filter]);

  const totalOverdue = allItems.filter(i => i.diffDays < 0).length;
  const totalUpcoming = allItems.length - totalOverdue;

  const toggle = () => {
    setOpen(prev => {
      writeStored(OPEN_STORAGE_KEY, prev ? '0' : '1');
      return !prev;
    });
  };

  const changeFilter = (key) => {
    setFilter(key);
    writeStored(FILTER_STORAGE_KEY, key);
  };

  const sendReminder = (item) => {
    const url = createWhatsAppURL(normalizePhoneForWhatsApp(item.phone1), buildReminderMessage(item));
    if (url) window.open(url, '_blank');
  };

  const setDone = (item, done) => {
    const cfg = TYPES[item.type];
    return updateDoc(doc(db, 'customers', item.customerId), {
      [cfg.doneField]: done,
      [cfg.doneAtField]: done ? new Date() : null,
      [cfg.doneByField]: done ? (user?.uid || null) : null,
      updatedAt: new Date(),
    });
  };

  const markDone = async (item) => {
    if (completingIds.includes(item.key)) return;
    setActionError(null);
    setCompletingIds(prev => [...prev, item.key]);
    await wait(COMPLETE_ANIMATION);
    setPendingIds(prev => [...prev, item.key]);
    setCompletingIds(prev => prev.filter(k => k !== item.key));
    try {
      await setDone(item, true);
      clearTimeout(undoTimerRef.current);
      setUndoItem(item);
      undoTimerRef.current = setTimeout(() => setUndoItem(null), UNDO_DURATION);
    } catch (err) {
      console.error('Yapıldı olarak işaretlenemedi:', err);
      setActionError('İşaretlenemedi. Bu işlem için yetkiniz olmayabilir.');
    } finally {
      setPendingIds(prev => prev.filter(k => k !== item.key));
    }
  };

  const undoDone = async () => {
    if (!undoItem) return;
    const item = undoItem;
    clearTimeout(undoTimerRef.current);
    setUndoItem(null);
    try {
      await setDone(item, false);
    } catch (err) {
      console.error('İşaret geri alınamadı:', err);
      setActionError('Geri alınamadı.');
    }
  };

  const renderRow = (item) => {
    const completing = completingIds.includes(item.key);
    const typeLabel = TYPES[item.type].label;
    return (
      <li key={item.key} className={`kr-row kr-tone-${item.status.tone} kr-type-${item.type}${completing ? ' is-completing' : ''}`}>
        <button
          type="button"
          className="kr-check"
          onClick={() => markDone(item)}
          aria-label={`${item.name || 'Müşteri'} ${typeLabel.toLocaleLowerCase('tr-TR')} yapıldı olarak işaretle`}
          title={`${typeLabel} yapıldı`}
        >
          <CheckIcon size={14} strokeWidth={3} />
        </button>

        <div className="kr-when">
          <span className={`kr-time${item.time ? '' : ' is-empty'}`}>{item.time || '--:--'}</span>
          <span className="kr-date">{formatShortDate(item.date)}</span>
        </div>

        <div className="kr-info">
          <div className="kr-name-line">
            <span className="kr-name">{item.name || 'İsimsiz müşteri'}</span>
            {!singleType && <span className={`kr-type kr-type-badge-${item.type}`}>{typeLabel}</span>}
            <span className={`kr-chip kr-chip-${item.status.tone}`}>{item.status.label}</span>
          </div>
          {item.address && (
            <span className="kr-address">
              <MapPinIcon size={13} />
              <span>{item.address}</span>
            </span>
          )}
        </div>

        {(item.phone1 || item.address) && (
          <div className="kr-actions">
            {item.phone1 && (
              <a className="kr-action" href={`tel:${item.phone1}`} aria-label="Ara" title="Ara">
                <PhoneIcon size={17} />
              </a>
            )}
            {item.phone1 && item.diffDays >= 0 && (
              <button
                type="button"
                className="kr-action kr-action-wa"
                onClick={() => sendReminder(item)}
                aria-label="WhatsApp hatırlatma gönder"
                title="WhatsApp hatırlatma gönder"
              >
                <MessageIcon size={17} />
              </button>
            )}
            {item.address && (
              <a
                className="kr-action"
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(item.address)}`}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`${item.name || 'Müşteri'} konumuna git`}
                title="Yol tarifi"
              >
                <NavigationIcon size={17} />
              </a>
            )}
          </div>
        )}
      </li>
    );
  };

  const title = singleType ? `${TYPES[singleType].label} Hatırlatıcı` : 'Keşif & Montaj Hatırlatıcı';
  const summary = loading
    ? 'Yükleniyor…'
    : [totalOverdue > 0 && `${totalOverdue} geciken`, `${totalUpcoming} yaklaşan`].filter(Boolean).join(' · ');

  return (
    <section className={`kr${open ? ' is-open' : ''}`}>
      <button type="button" className="kr-header" onClick={toggle} aria-expanded={open}>
        <span className="kr-header-icon">
          <CalendarCheckIcon size={20} />
        </span>
        <span className="kr-header-text">
          <span className="kr-header-title">{title}</span>
          <span className="kr-header-summary">{summary}</span>
        </span>
        {!loading && totalOverdue > 0 && (
          <span className="kr-header-badge" title="Geciken">
            <AlertIcon size={13} strokeWidth={2.25} />
            {totalOverdue}
          </span>
        )}
        <span className="kr-header-chevron"><ChevronDownIcon size={18} /></span>
      </button>

      <div className="kr-collapse">
        <div className="kr-collapse-inner">
          <div className="kr-body">
            {!singleType && (
              <div className="kr-filter" role="group" aria-label="Tür">
                {FILTERS.map(f => (
                  <button
                    key={f.key}
                    type="button"
                    className={filter === f.key ? 'is-active' : ''}
                    aria-pressed={filter === f.key}
                    onClick={() => changeFilter(f.key)}
                  >
                    {f.label}
                    <span>{counts[f.key]}</span>
                  </button>
                ))}
              </div>
            )}

            {error && <div className="kr-message kr-message-error">{error}</div>}
            {actionError && <div className="kr-message kr-message-error">{actionError}</div>}

            {!loading && !error && overdue.length === 0 && upcoming.length === 0 && (
              <div className="kr-empty">
                <span className="kr-empty-icon"><ClockIcon size={22} /></span>
                <strong>Bekleyen iş yok</strong>
                <span>Müşteri kaydında keşif veya teslimat/montaj tarihi seçildiğinde burada listelenir.</span>
              </div>
            )}

            {overdue.length > 0 && (
              <div className="kr-section">
                <div className="kr-section-label kr-section-label-danger">
                  Geciken <span>{overdue.length}</span>
                </div>
                <ul className="kr-list">{overdue.map(renderRow)}</ul>
              </div>
            )}

            {upcoming.length > 0 && (
              <div className="kr-section">
                <div className="kr-section-label">
                  Yaklaşan <span>{upcoming.length}</span>
                </div>
                <ul className="kr-list">{upcoming.map(renderRow)}</ul>
              </div>
            )}
          </div>
        </div>
      </div>

      {undoItem && (
        <div className="kr-toast" role="status">
          <span className="kr-toast-icon"><CheckIcon size={14} strokeWidth={3} /></span>
          <span className="kr-toast-text">
            <strong>{undoItem.name || 'Müşteri'}</strong> {TYPES[undoItem.type].doneNoun} tamamlandı
          </span>
          <button type="button" className="kr-toast-undo" onClick={undoDone}>
            <UndoIcon size={15} /> Geri al
          </button>
        </div>
      )}
    </section>
  );
};

export default PlanReminders;
